import { useMemo, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { DisclosureExpand } from '@sas/react-svg';
import { setAlpha } from '../../design-tokens';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useTruncationTooltip } from '../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import { Button, ButtonVariant } from '../Button';
import { Icon } from '../Icon';
import { OptionPopper } from '../OptionPopper';
import bundle from './NovaComponents-VerticalBreadcrumb-gui-icu.properties';
import cn from './VerticalBreadcrumb.css.json';
import './VerticalBreadcrumb.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useListId = createUseHtmlId('VerticalBreadcrumbList');
export const VerticalBreadcrumb = props => {
  const localize = useTextLocalizer();
  const listId = useListId();
  const breadcrumbRef = useRef(null);
  const ulRef = useRef(null);
  const setTruncationRef = useTruncationTooltip();
  const items = useMemo(() => {
    const values = props.values || [];
    return values.map((v, i) => ({
      text: v.values[v.selectedIndex],
      icon: v.icon?.icon,
      originalIndex: i,
      className: cn.defaultCursor,
      iconTooltip: v.icon?.tooltip ?? '',
      iconAriaLabel: v.icon?.ariaLabel
    })).reverse();
  }, [props.values]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const handleBlur = e => {
    if (ulRef.current && ulRef.current.contains(e.relatedTarget)) return;
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsOpen(false);
    }
  };
  const dt = useDesignTokens();
  const tokens = {
    ...getVerticalBreadcrumbDesignTokens(dt, {
      tokens: props.tokens,
      variant: props.variant
    }),
    ...props.theme?.(dt)
  };
  const buttonVariant = dt.themeVariant === 'light' && props.variant === 'primary-light' ? ButtonVariant.Primary : ButtonVariant.Standard;
  const tokenOverrides = props.tokens?.();
  const currentItem = items.length ? items[isNullish(focusedIndex) ? 0 : focusedIndex] : null;
  const currentText = currentItem ? currentItem.text : '';
  const onKeyDown = e => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case ' ':
        // Open dropdown on arrowKey, alt+arrowKey, or space
        if (!e.ctrlKey && !e.shiftKey && !e.metaKey) {
          setIsOpen(true);
          e.preventDefault();
          e.stopPropagation();
        }
        break;
    }
  };
  const closePopover = () => {
    setIsOpen(false);
    setTimeout(() => {
      breadcrumbRef.current?.focus();
    }, 0);
    setFocusedIndex(0);
  };
  const selectBreadcrumb = index => {
    const selectedItem = items[index];
    props.onPress?.(selectedItem.originalIndex);
  };
  return /*#__PURE__*/_jsxs("div", {
    onBlur: handleBlur,
    style: {
      ...tokens,
      ...props.style
    },
    children: [/*#__PURE__*/_jsxs("div", {
      ref: breadcrumbRef,
      onKeyDown: onKeyDown,
      className: cn.vertical,
      role: "combobox",
      "aria-controls": listId,
      "aria-expanded": isOpen,
      "aria-readonly": true,
      "aria-label": props.ariaLabel ?? localize('container.aria.label'),
      "aria-haspopup": true,
      tabIndex: 0,
      "data-testid": postfixTestId(props.testId, 'container'),
      children: [/*#__PURE__*/_jsx(Button, {
        onPress: () => setIsOpen(prevIsOpen => !prevIsOpen),
        className: cn.verticalButton,
        icon: {
          icon: DisclosureExpand
        },
        variant: buttonVariant,
        theme: () => {
          const overrides = tokenOverrides?.fg ? {
            '--button-fg': tokenOverrides?.fg,
            '--button-hover-fg': tokenOverrides?.fg,
            '--button-active-fg': tokenOverrides?.fg
          } : undefined;
          if (!dt.enableNovaTokens) return overrides;
          if (dt.themeVariant === 'light' && props.variant === 'primary-light') {
            return {
              '--button-bg': setAlpha(dt.tokens['color-text-primary-inverse'], 0.1),
              '--button-hover-bg': setAlpha(dt.tokens['color-text-primary-inverse'], 0.15),
              '--button-active-bg': setAlpha(dt.tokens['color-text-primary-inverse'], 0.25),
              '--button-bd': dt.tokens['color-container-border-4'],
              '--button-hover-bd': dt.tokens['color-container-border-4'],
              '--button-active-bd': dt.tokens['color-container-border-4'],
              ...overrides
            };
          } else if (dt.themeVariant === 'high-contrast' && isOpen) {
            return {
              '--button-bg': dt.tokens['color-action-background-standard-selected'],
              '--button-fg': dt.tokens['color-action-text-standard-selected'],
              '--button-hover-fg': dt.tokens['color-action-text-standard-selected'],
              '--button-hover-bg': dt.tokens['color-action-background-standard-selected'],
              '--button-filter': 'none',
              ...overrides
            };
          }
          return overrides;
        },
        focusable: false,
        "aria-hidden": true,
        testId: postfixTestId(props.testId, 'button')
      }), /*#__PURE__*/_jsxs("div", {
        "aria-live": isOpen ? undefined : 'polite',
        className: cn.verticalField,
        children: [currentItem?.icon && /*#__PURE__*/_jsx(Icon, {
          className: cn.verticalFieldIcon,
          icon: currentItem.icon,
          tooltip: currentItem.iconTooltip,
          ariaLabel: currentItem.iconAriaLabel
        }), /*#__PURE__*/_jsx("div", {
          className: cn.currentText,
          ref: setTruncationRef,
          children: currentText
        })]
      })]
    }), /*#__PURE__*/_jsx(OptionPopper, {
      isOpen: isOpen,
      displayListItemIcon: true,
      listItemFocusable: true,
      onClose: closePopover,
      focusLockProps: {
        disabled: true
      },
      focusedIndex: focusedIndex,
      setFocusedIndex: setFocusedIndex,
      onSelectFocusedItem: () => {
        // NOTE: this fires after the onPress Button event handler
        //     | the isOpen check happens before that event handler's setState calls are committed
        if (isOpen) {
          !isNullish(focusedIndex) && selectBreadcrumb(focusedIndex);
          closePopover();
        }
      },
      onSelected: ({
        index
      }) => {
        selectBreadcrumb(index);
        closePopover();
      },
      items: items,
      referenceElementRef: breadcrumbRef,
      ulRef: ulRef,
      showSelectedPseudoFocus: true,
      listId: listId,
      listTokens: {
        font: {
          fontFamily: tokenOverrides?.fontFamily
        },
        ...(tokenOverrides?.fg && {
          theme: () => ({
            fg: tokenOverrides?.fg
          })
        })
      },
      testId: props.testId,
      ariaLabel: localize('list.aria')
    })]
  });
};
export const getVerticalBreadcrumbDesignTokens = (dt, opts) => {
  let tokens = {
    bg: dt.tokens['color-action-background-control-non-editable'],
    fg: dt.tokens['color-text-neutral'],
    bd: dt.tokens['color-action-border-control-read-only']
  };
  if (dt.themeVariant === 'light' && opts?.variant === 'primary-light') {
    tokens = {
      ...tokens,
      fg: dt.tokens['color-text-primary-inverse'],
      bg: setAlpha(dt.tokens['color-text-primary-inverse'], 0.15)
    };
  }
  const tokenOverrides = opts?.tokens?.();
  if (tokenOverrides?.fg) {
    tokens = {
      ...tokens,
      fg: tokenOverrides.fg
    };
  }
  const outlineOffset = 'calc(-2px + calc(-1 * var(--focus-width)))';
  return {
    ...renderDesignTokens({
      ...tokens,
      'squish-s': dt.tokens['space-squish-s'],
      'mg-xs': dt.tokens['space-base-xs'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      'font-family': tokenOverrides?.fontFamily ?? 'inherit'
    }, 'breadcrumb'),
    ...renderDesignTokens({
      outline: 'var(--focus-light-bg)',
      'outline-offset': outlineOffset
    }, 'button-focus'),
    ...renderDesignTokens(tokens, 'vertical-field'),
    ...(!dt.enableNovaTokens && getVerticalBreadcrumbLegacyDesignTokens(dt, opts))
  };
};
const getVerticalBreadcrumbLegacyDesignTokens = (dt, opts) => {
  let tokens = {};
  switch (dt.baseReportThemeName) {
    case 'highcontrast2018':
    case 'highcontrast2017':
      tokens = {
        ...dt.reportTokens.stateStandard.normal,
        bg: dt.reportTokens.stateSecondary.normal.bg
      };
      break;
    default:
      switch (dt.themeVariant) {
        case 'dark':
          tokens = {
            ...dt.reportTokens.stateStandard.normal,
            bg: setAlpha(dt.c.white, 0.1)
          };
          break;
        case 'high-contrast':
          tokens = {
            ...dt.reportTokens.stateStandard.normal,
            bg: dt.c.white20
          };
          break;
        default:
          tokens = {
            ...dt.reportTokens.stateStandard.normal,
            bg: setAlpha(dt.c.n2, 0.4)
          };
          break;
      }
  }
  const tokenOverrides = opts?.tokens?.();
  if (tokenOverrides?.fg) {
    tokens = {
      ...tokens,
      fg: tokenOverrides.fg
    };
  }
  return {
    ...renderDesignTokens({
      'font-size-m': dt.reportTokens.font.size.m
    }, 'breadcrumb'),
    ...renderDesignTokens(tokens, 'vertical-field')
  };
};
//# sourceMappingURL=VerticalBreadcrumb.js.map